import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { isSignedIn } from '../../../_helpers/safeRedirect.js';
import config from '../../../config.js';
import '../../../styles/SaveCardBanner.css';

/** The query Stripe's card setup comes back with (ENG-704): ?card=saved|cancelled. */
const RETURN_PARAM = 'card';

/** Reads and removes ?card=… from the address bar; returns its value. */
const takeReturnParam = () => {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const value = url.searchParams.get(RETURN_PARAM);
  if (value == null) return null;
  url.searchParams.delete(RETURN_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return value;
};

/**
 * "SONGSHAKE · Save a card. Tip in one tap." pinned at the top of the stream
 * chat (mockup 19, ENG-703). Shown only to a signed-in listener with no saved
 * card who hasn't closed it (`GET /me/payment-method`, ENG-704). Save goes to
 * Stripe's hosted card setup (`POST /me/payment-method/setup`) and comes back
 * here; × is remembered on the account (`POST /me/payment-method/banner-
 * dismissal`), so it stays closed in every session.
 */
const SaveCardBanner = () => {
  const { t } = useLocale();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Back from setup: the card is saved (or the listener backed out); either
    // way the banner has had its turn this visit.
    if (takeReturnParam() != null || !isSignedIn()) return undefined;
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me/payment-method`)
      .then((card) => {
        if (!cancelled) setVisible(Boolean(card) && !card.hasSavedCard && !card.bannerDismissed);
      })
      .catch(() => {
        if (!cancelled) setVisible(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (!visible) return null;

  const save = async () => {
    setSaving(true);
    setError(false);
    try {
      const { checkout_url: url } = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/me/payment-method/setup`, {
        return_path: window.location.pathname,
      });
      window.location.assign(url);
    } catch (_err) {
      setSaving(false);
      setError(true);
    }
  };

  const dismiss = () => {
    setVisible(false);
    // Closing works even if remembering it fails; it comes back next visit then.
    fetchWrapperRef.current.post(`${config.apiBaseUrl}/me/payment-method/banner-dismissal`, {}).catch(() => {});
  };

  return (
    <div className="save-card-banner" data-testid="save-card-banner" role="region" aria-label={t('stream.saveCard.label')}>
      <span className="save-card-banner-brand">{t('stream.saveCard.brand')}</span>
      <span className="save-card-banner-dot" aria-hidden="true">·</span>
      <span className="save-card-banner-text">{error ? t('stream.saveCard.error') : t('stream.saveCard.pitch')}</span>
      <button type="button" className="save-card-banner-save" onClick={save} disabled={saving} data-testid="save-card-banner-save">
        {t('stream.saveCard.save')}
      </button>
      <button type="button" className="save-card-banner-close" onClick={dismiss} aria-label={t('stream.saveCard.dismiss')} data-testid="save-card-banner-close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
};

export default SaveCardBanner;
